import React, { useState } from "react";
import { toast } from "react-toastify";

const PatientConsent = ({
  formData,
  setFormData,
  isConsentAcepted,
  setConsentAccepted,
}) => {
  const [isConsentVisible, setIsConsentVisible] = useState(false);

  // Handles changes for all form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    toast.success("Consent form submitted successfully!");
  };

  return (
    <div className="">
      <div className="max-w-4xl mx-auto">
        <label className="text-md">
          Patient Consent for Digital Health and Home Care Services
        </label>
        <div className="flex items-center justify-between">
          {/* --- Checkbox to show the form --- */}
          <div className="flex items-center">
            <input
              id="show-form-checkbox"
              type="checkbox"
              checked={isConsentAcepted}
              onChange={() => setConsentAccepted((pre) => !pre)}
              className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label
              htmlFor="show-form-checkbox"
              className="ml-3 text-sm font-medium text-gray-800"
            >
            I agree to these terms.
            </label>
          </div>
          <p
            className="cursor-pointer text-sm text-blue-600 underline"
            onClick={() => setIsConsentVisible(!isConsentVisible)}
          >
            Read More
          </p>
        </div>

        {/* --- Conditionally Rendered Consent Content --- */}
        {isConsentVisible && (
          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 prose prose-sm max-w-none mt-4">
            <ol className="list-decimal list-inside space-y-3">
              <li>
                <strong>Purpose of Services:</strong> I understand that this
                app/digital platform enables healthcare services such as
                teleconsultation, remote monitoring, home care, pediatric care,
                and related medical or wellness services provided by the clinic,
                hospital, or doctor with whom I am registered. These services
                are designed to enhance access, convenience, and continuity of
                care.
              </li>
              <li>
                <strong>Nature and Mode of Service Delivery:</strong> I
                acknowledge that care may be provided through digital
                means—including video, audio, chat, or connected medical
                devices. Such digital interactions are intended to complement,
                not replace, physical medical consultations when clinically
                required.
              </li>
              <li>
                <strong>Technology Platform:</strong> I understand that my
                doctor, clinic, or hospital uses a licensed digital platform to
                manage appointments, remote monitoring data, communications, and
                electronic health records. This platform is operated and
                maintained by a technology partner that enables secure,
                compliant healthcare delivery.
              </li>
              <li>
                <strong>Data Collection and Use:</strong> I consent to the
                collection, processing, storage, and use of my personal and
                health information for the purposes of delivering and
                coordinating healthcare services, communicating with me and my
                authorized caregivers, improving clinical quality, and
                fulfilling regulatory requirements.
              </li>
              <li>
                <strong>Sharing of Medical Information:</strong> I authorize my
                doctor, clinic, or hospital to share relevant medical
                information with other authorized healthcare professionals,
                caregivers, or partners for the continuity of my care.
              </li>
              <li>
                <strong>Data Privacy and Security:</strong> All personal and
                medical data will be handled in accordance with applicable data
                protection and healthcare privacy laws.
              </li>
              <li>
                <strong>Risks and Limitations:</strong> I understand that
                digital healthcare services may have limitations and agree to
                seek emergency or in-person care if my condition worsens.
              </li>
              <li>
                <strong>Voluntary Participation and Withdrawal:</strong> I
                confirm that my participation is voluntary. I may withdraw
                consent at any time by notifying the doctor/clinic/hospital.
              </li>
              <li>
                <strong>Declaration:</strong> I declare that the information
                provided by me is accurate to the best of my knowledge. I am the
                patient, parent or legally authorized guardian giving this
                consent.
              </li>
            </ol>
            <div className="mt-4">
              {/* --- Conditionally Rendered Form --- */}
              {isConsentAcepted && (
                <form
                  onSubmit={handleSubmit}
                  className="mt-6 space-y-5"
                >
                  <hr></hr>
                  <p className="text-xs">
                    Complete following information If you are a parent or legal
                    guardian to the patient
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* First Name */}
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  {/* Mobile Number */}
                  <div>
                    <label
                      htmlFor="mobileNumber"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Mobile Number
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="text"
                        name="mobileCode"
                        id="mobileCode"
                        value={formData.mobileCode}
                        onChange={handleInputChange}
                        placeholder="+91"
                        className="block w-1/4 rounded-none rounded-l-md px-3 py-2 border border-r-0 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                      <input
                        type="tel"
                        name="mobileNumber"
                        id="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        required
                        className="block w-3/4 rounded-none rounded-r-md px-3 py-2 border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Relation Dropdown */}
                  <div>
                    <label
                      htmlFor="relation"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Relation
                    </label>
                    <select
                      id="relation"
                      name="relation"
                      value={formData.relation}
                      onChange={handleInputChange}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option>Parent</option>
                      <option>Spouse</option>
                      <option>Daughter</option>
                      <option>Son</option>
                      <option>Neighbour</option>
                      <option>Legal Guardian</option>
                      <option>Other</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <div>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Submit Information
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientConsent;
